// ============================================================================
// Address Schema — Shared across onboarding, tenant admin, invoicing, equipment
// Rule 15: Single Zod schema, reused in 15+ locations
// ============================================================================

import { z } from 'zod';

/**
 * Base address schema — all address fields optional by default.
 * Consumers call .required() or .extend() for stricter validation.
 */
export const addressSchema = z.object({
  addressLine1: z.string().optional(),
  addressLine2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  countryCode: z.string().min(2).max(3).optional(),
});

/**
 * Address schema with required fields for business address.
 */
export const requiredAddressSchema = z.object({
  addressLine1: z.string().min(1, 'Street address is required'),
  addressLine2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  postalCode: z.string().min(1, 'Postal code is required'),
  countryCode: z.string().min(2, 'Country is required'),
});

/**
 * Billing address schema — used when "Use different billing address" is checked.
 */
export const billingAddressSchema = z.object({
  billingAddressLine1: z.string().optional(),
  billingAddressLine2: z.string().optional(),
  billingCity: z.string().optional(),
  billingState: z.string().optional(),
  billingPostalCode: z.string().optional(),
  billingCountryCode: z.string().optional(),
});

export type Address = z.infer<typeof addressSchema>;
export type RequiredAddress = z.infer<typeof requiredAddressSchema>;
export type BillingAddress = z.infer<typeof billingAddressSchema>;
